/**
 * @file myTodosApi.ts
 * @brief "My to-dos": the read (`GET /boardroom/my-todos`) and the tick.
 *
 * @details
 * One list of the to-dos from every meeting the user can see, grouped by due
 * date on the server in the browser's own time zone (`tz`), so "Today" is the
 * user's today. Two views share the key prefix `["my-todos"]`:
 * - Home (`view=home`): overdue / today / tomorrow, max 5; else the next 3.
 * - The `/todos` page: one tab (`status`), `scope`, search `q`, `limit=300`.
 *
 * Items carry `access` ("meeting" | "todo") and `fromName` (12-10-2026): a
 * to-do-only user sees the to-do, never the meeting.
 *
 * Ticking is the meeting's own `PATCH /boardroom/sessions/:id/todos/:todoId
 * { completed }`, the call the meeting detail's checkbox makes. The tick
 * here does NOT refresh `["my-todos"]` itself: the list keeps a ticked row,
 * struck through, for ~1.5s and refreshes when it leaves (MyTodosList). It
 * does refresh both meeting-detail caches (company + admin, see
 * `meetingDetailKeys`) and the meeting lists, whose open/done counts follow.
 *
 * @author DES
 * @date 12-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, apiPatch } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
import { companyMeetingsKeys, meetingDetailKeys } from "@/features/company-meetings/api";
import { adminMeetingsKeys } from "@/features/admin-meetings/api";

export type MyTodosStatus = "open" | "overdue" | "done";
export type MyTodosScope = "mine" | "all";

export type MyTodosParams = {
  status?: MyTodosStatus;
  scope?: MyTodosScope;
  q?: string;
  view?: "home";
  limit?: number;
};

const nullableString = z.string().nullable().optional();

const MyTodoItemSchema = z
  .object({
    meetingId: z.string(),
    meetingTitle: nullableString,
    meetingDate: nullableString,
    todoId: z.string(),
    task: nullableString,
    source: nullableString,
    dueDate: nullableString,
    dueSource: nullableString,
    group: z.string(),
    role: z.string().catch("viewer"),
    canTick: z.boolean().catch(false),
    assigneeName: nullableString,
    followUpName: nullableString,
    completed: z.boolean().catch(false),
    completedAt: nullableString,
    timeInSecs: z.number().nullable().optional(),
    /** "todo" = this to-do only, never its meeting (12-10-2026); absent from
     *  an older server = full meeting access. */
    access: z.enum(["meeting", "todo"]).catch("meeting"),
    /** Who gave the to-do: whoever typed it, else the meeting owner. */
    fromName: nullableString,
    /** F1a (ADR-062): null until the meeting is labelled. */
    topic: nullableString,
    categoryId: nullableString,
    category: nullableString,
    /** B53: the server's lock (owner's window for someone else's meeting,
     *  mine for my own); absent from an older server = B39 date check. */
    historyLocked: z.boolean().optional().catch(undefined),
  })
  .passthrough();
export type MyTodoItem = z.infer<typeof MyTodoItemSchema>;

const MyTodosResponseSchema = z
  .object({
    today: z.string().nullable().optional(),
    status: z.string().optional(),
    scope: z.string().optional(),
    counts: z
      .object({
        open: z.number().catch(0),
        overdue: z.number().catch(0),
        done: z.number().catch(0),
      })
      .catch({ open: 0, overdue: 0, done: 0 }),
    truncated: z.boolean().catch(false),
    groups: z
      .array(z.object({ key: z.string(), items: z.array(MyTodoItemSchema) }))
      .catch([]),
  })
  .passthrough();
export type MyTodosResponse = z.infer<typeof MyTodosResponseSchema>;

export const myTodosKeys = {
  all: ["my-todos"] as const,
  list: (params: MyTodosParams) => ["my-todos", params] as const,
};

/** The browser's IANA zone; the server buckets due dates in it. */
function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function myTodosUrl(params: MyTodosParams): string {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.scope) qs.set("scope", params.scope);
  if (params.q?.trim()) qs.set("q", params.q.trim());
  if (params.view) qs.set("view", params.view);
  if (params.limit) qs.set("limit", String(params.limit));
  qs.set("tz", browserTimeZone());
  return `${boardroomEndpoints.myTodos}?${qs.toString()}`;
}

export function useMyTodos(params: MyTodosParams, enabled = true) {
  return useQuery({
    queryKey: myTodosKeys.list(params),
    enabled,
    staleTime: 60_000,
    // A search or tab change keeps the last rows up until the new ones land.
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const raw = await apiGet<unknown>(myTodosUrl(params));
      return MyTodosResponseSchema.parse(raw);
    },
  });
}

export type TickMyTodoVars = { meetingId: string; todoId: string; completed: boolean };

/** Tick / untick one to-do; refreshes the meeting's caches, not this list. */
export function useTickMyTodo() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, TickMyTodoVars>({
    // The reply is not read: it is the meeting, or only `{ todo }` for a
    // to-do-only user, and the caches below are refetched either way.
    mutationFn: ({ meetingId, todoId, completed }) =>
      apiPatch<unknown>(boardroomEndpoints.toggleTodo(meetingId, todoId), { completed }),
    onSuccess: (_data, { meetingId }) => {
      // Both pages that cache this meeting (webpanel-meeting-detail-two-query-keys).
      for (const key of meetingDetailKeys(meetingId)) {
        void qc.invalidateQueries({ queryKey: key });
      }
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
      void qc.invalidateQueries({ queryKey: adminMeetingsKeys.all });
    },
  });
}
